import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ProjectListItem } from '@hygge/shared';
import { Archive, ArchiveRestore, MoreHorizontal, Pencil, Users } from 'lucide-react';

interface ProjectCardProps {
  project: ProjectListItem;
  onOpen: (id: string) => void;
  onEdit: (project: ProjectListItem) => void;
  onArchive: (project: ProjectListItem) => void;
  onUnarchive: (project: ProjectListItem) => void;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' },
  on_hold: { label: 'On Hold', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  archived: { label: 'Archived', className: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20' },
};

function getInitials(firstName: string, lastName: string) {
  return `${firstName[0]}${lastName[0]}`.toUpperCase();
}

export function ProjectCard({ project, onOpen, onEdit, onArchive, onUnarchive }: ProjectCardProps) {
  const formatName = useEmployeeNameFormatter();
  const status = statusConfig[project.status] || statusConfig.active;

  return (
    <Card
      className="group relative cursor-pointer transition-all hover:shadow-md hover:border-border/80"
      onClick={() => onOpen(project.id)}
    >
      <div className="absolute inset-x-0 top-0 h-1 rounded-t-lg" style={{ backgroundColor: project.color }} />
      <CardContent className="p-5 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-sm truncate">{project.name}</h3>
              <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0', status.className)}>
                {status.label}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground font-mono">{project.slug}</p>
          </div>

          <DropdownMenu>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer shrink-0"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Options</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem className="cursor-pointer" onClick={() => onEdit(project)}>
                <Pencil />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {project.status === 'archived' ? (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onUnarchive(project)}
                >
                  <ArchiveRestore />
                  Unarchive
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onArchive(project)}
                >
                  <Archive />
                  Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {project.description && (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{project.description}</p>
        )}

        <div className="mt-4 flex items-center justify-between">
          {/* Project managers */}
          <div
            className="flex items-center gap-1.5 min-w-0"
            title={project.managers.map((m) => `${m.firstName} ${m.lastName}`).join(', ')}
          >
            {project.managers.length === 0 ? (
              <span className="text-xs text-muted-foreground italic">No manager</span>
            ) : (
              <>
                <div className="flex -space-x-1.5 shrink-0">
                  {project.managers.slice(0, 3).map((pm) => (
                    <Avatar key={pm.id} className="h-5 w-5 ring-1 ring-card">
                      <AvatarImage src={pm.avatarUrl || undefined} />
                      <AvatarFallback className="text-[9px]">
                        {getInitials(pm.firstName, pm.lastName)}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                </div>
                <span className="text-xs text-muted-foreground truncate">
                  {formatName({ firstName: project.managers[0].firstName, lastName: project.managers[0].lastName })}
                  {project.managers.length > 1 && ` +${project.managers.length - 1}`}
                </span>
              </>
            )}
          </div>

          {/* Members count + avatars */}
          <div className="flex items-center gap-1.5 shrink-0">
            <TooltipProvider delayDuration={0}>
              <div className="flex -space-x-1.5">
                {project.members.slice(0, 3).map((m) => (
                  <Tooltip key={m.id}>
                    <TooltipTrigger asChild>
                      <Avatar className="h-5 w-5 border-2 border-card">
                        <AvatarImage src={m.avatarUrl || undefined} />
                        <AvatarFallback className="text-[8px]">
                          {getInitials(m.firstName, m.lastName)}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {formatName({ firstName: m.firstName, lastName: m.lastName })}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
            <span className="text-xs text-muted-foreground">
              <Users className="inline h-3 w-3 mr-0.5" />
              {project.memberCount}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
